import { useQuery } from '@tanstack/react-query'
import { PackageOpen, RefreshCw, ShoppingCart, TrendingUp } from 'lucide-react'
import { useState } from 'react'

import { purchasesService, SuggestedProduct } from '../../services/purchases.service'
import { BadgeListPopover } from '../ui/BadgeListPopover'
import { GlobalFilterBar } from '../ui/GlobalFilterBar'
import { Button } from '../ui/button'
import { DiscreteLoader } from '../ui/discrete-loader'
import { GlobalPaginator } from '../ui/global-paginator'
import { Input } from '../ui/input'
import { TabsContent } from '../ui/tabs'

const perPage = 10

function localDate(date: Date): string {
  const offset = date.getTimezoneOffset()
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10)
}

function initialPeriod(): { dateFrom: string; dateTo: string } {
  const dateTo = new Date()
  const dateFrom = new Date(dateTo)
  dateFrom.setDate(dateFrom.getDate() - 29)

  return { dateFrom: localDate(dateFrom), dateTo: localDate(dateTo) }
}

function compactNumber(value: number): string {
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 }).format(value)
}

function StockBreakdown({ product }: { product: SuggestedProduct }) {
  const items = [
    { id: 'total', name: `Total: ${product.currentStock} u.`, shortName: `${product.currentStock} u.` },
    ...product.branches.map((branch) => ({
      id: branch.id,
      name: `${branch.name}: ${branch.currentStock} u.`,
    })),
  ]

  return (
    <BadgeListPopover
      items={items}
      popoverTitle="Stock por sucursal"
      icon={<PackageOpen className="h-4 w-4 shrink-0 text-card-description" />}
    />
  )
}

function SalesBreakdown({ product }: { product: SuggestedProduct }) {
  const items = [
    {
      id: 'total',
      name: product.avgSales > 0 ? `Total: ${compactNumber(product.avgSales)}/día` : 'Total: Sin ventas',
      shortName: product.avgSales > 0 ? `${compactNumber(product.avgSales)}/día` : 'Sin ventas',
    },
    ...product.branches.map((branch) => ({
      id: branch.id,
      name: `${branch.name}: ${branch.avgSales > 0 ? `${compactNumber(branch.avgSales)}/día` : 'Sin ventas'}`,
    })),
  ]

  return <BadgeListPopover items={items} popoverTitle="Venta promedio por sucursal" />
}

function SuggestedBreakdown({ product }: { product: SuggestedProduct }) {
  const items = [
    { id: 'total', name: `Total: ${product.suggested} u.`, shortName: `${product.suggested} u.` },
    ...product.branches.map((branch) => ({
      id: branch.id,
      name: `${branch.name}: ${branch.suggested} u.`,
    })),
  ]

  return (
    <BadgeListPopover
      items={items}
      popoverTitle="Cantidad sugerida por sucursal"
      icon={<ShoppingCart className="h-4 w-4 shrink-0 text-card-description" />}
    />
  )
}

export function SuggestedOrdersTab({ active = true }: { active?: boolean }) {
  const [filters, setFilters] = useState(initialPeriod)
  const [page, setPage] = useState(1)
  const suggestionsQuery = useQuery({
    queryKey: ['purchase-suggested-orders', filters, page],
    queryFn: () => purchasesService.getSuggestedProducts({
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
      page,
      per_page: perPage,
    }),
    enabled: active,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })

  const paginator = suggestionsQuery.data?.data
  const products = paginator?.data ?? []

  return (
    <TabsContent value="suggested" className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-start gap-3">
          <TrendingUp className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <h3 className="font-medium text-card-foreground">Pedidos basados en rotación</h3>
            <p className="mt-1 text-sm text-card-description">
              Estos productos requieren reabastecimiento según su velocidad de venta
            </p>
          </div>
        </div>
      </div>

      <GlobalFilterBar
          show={[]}
          values={{}}
          onChange={() => {}}
        >
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-card-foreground">
              Fecha Inicio
            </label>

            <Input
              aria-label="Fecha Inicio"
              type="date"
              value={filters.dateFrom}
              max={filters.dateTo}
              onChange={(event) => {
                setFilters((current) => ({
                  ...current,
                  dateFrom: event.target.value,
                }))
                setPage(1)
              }}
              className="h-10 w-[180px] bg-card text-card-foreground"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-card-foreground">
              Fecha Fin
            </label>

            <Input
              aria-label="Fecha Fin"
              type="date"
              value={filters.dateTo}
              min={filters.dateFrom}
              onChange={(event) => {
                setFilters((current) => ({
                  ...current,
                  dateTo: event.target.value,
                }))
                setPage(1)
              }}
              className="h-10 w-[180px] bg-card text-card-foreground"
            />
          </div>
        </GlobalFilterBar>

      {suggestionsQuery.isLoading ? (
        <div className="min-h-64 rounded-lg border bg-white">
          <DiscreteLoader text="Calculando pedidos sugeridos..." />
        </div>
      ) : suggestionsQuery.isError ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="font-medium text-red-800">No fue posible calcular los pedidos sugeridos</p>
          <Button variant="outline" className="mt-4" onClick={() => void suggestionsQuery.refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
          </Button>
        </div>
      ) : products.length === 0 ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-table-border bg-table-bg p-6 text-center">
          <ShoppingCart className="mb-3 h-10 w-10 text-card-description" />
           <p className="font-medium text-table-foreground">No hay productos que requieran reabastecimiento</p>
            <p className="mt-1 text-sm text-table-foreground/70">
              Revisa el periodo seleccionado; se requieren ventas netas y existencias menores a la rotación diaria.
            </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                <tr>
                  {['Producto', 'Stock Actual', 'Venta Promedio', 'Días Rest.', 'Cant. Sugerida', 'Proveedor'].map((heading) => (
                    <th key={heading} className="px-6 py-3 text-left text-xs font-medium uppercase text-table-header-foreground">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {products.map((product) => (
                  <tr key={product.id} className="transition-colors hover:bg-table-row-hover">
                    <td className="px-6 py-4"><p className="font-medium text-table-foreground">{product.name}</p>{product.barcode && (<p className="mt-1 font-mono text-xs text-muted-foreground">{product.barcode}</p>)}</td>
                    <td className="px-6 py-4"><StockBreakdown product={product} /></td>
                    <td className="px-6 py-4"><SalesBreakdown product={product} /></td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${product.daysRemaining !== null && product.daysRemaining < 1 ? 'text-red-400' : 'text-table-foreground'}`}>
                        {product.daysRemaining === null ? 'Sin ventas' : product.daysRemaining.toFixed(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-semibold text-primary"><SuggestedBreakdown product={product} /></td>
                    <td className="px-6 py-4 text-sm text-table-foreground">{product.supplier?.name ?? 'Sin proveedor con costo'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <GlobalPaginator
            totalItems={paginator?.total ?? 0}
            itemsPerPage={paginator?.per_page ?? perPage}
            currentPage={paginator?.current_page ?? page}
            from={paginator?.from ?? 0}
            to={paginator?.to ?? 0}
            onPageChange={setPage}
          />
        </div>
      )}
    </TabsContent>
  )
}
